import { TechNode } from '../../types';

/**
 * Space domain nodes
 * The path to becoming a multi-planetary species
 */
export const SPACE_NODES: TechNode[] = [
  // === FOUNDATIONAL (Tier 0) ===
  {
    id: 'space-orbital',
    name: 'Orbital Spaceflight',
    description: 'Sustained presence in Earth orbit',
    domain: 'space',
    status: 'achieved',
    yearAchieved: 1961,
    importance: 8,
    tier: 0,
    trl: 9,
    milestones: [
      {
        date: '1961-04-12',
        title: 'Vostok 1',
        description: 'Yuri Gagarin first human in space and Earth orbit',
        significance: 'breakthrough',
        source: 'https://www.nasa.gov/mission_pages/station/research/benefits/gagarin_anniversary',
      },
    ],
  },
  {
    id: 'space-moon-landing',
    name: 'Moon Landing',
    description: 'Human presence on lunar surface',
    domain: 'space',
    status: 'achieved',
    yearAchieved: 1969,
    importance: 9,
    tier: 0,
    trl: 9,
    milestones: [
      {
        date: '1969-07-20',
        title: 'Apollo 11',
        description: 'First humans walk on the Moon',
        significance: 'breakthrough',
        source: 'https://www.nasa.gov/mission_pages/apollo/apollo11.html',
      },
    ],
  },

  // === REUSABILITY (Tier 1) ===
  {
    id: 'space-reusable-rockets',
    name: 'Reusable Rockets',
    description: 'Rockets that can be flown multiple times',
    domain: 'space',
    status: 'achieved',
    yearAchieved: 2015,
    importance: 9,
    tier: 1,
    trl: 9,
    keyPlayers: ['SpaceX', 'Rocket Lab', 'Blue Origin'],
    milestones: [
      {
        date: '2015-12-21',
        title: 'Falcon 9 Landing',
        description: 'First orbital rocket booster landing',
        significance: 'breakthrough',
        source: 'https://www.spacex.com/updates/',
      },
    ],
  },
  {
    id: 'space-starship',
    name: 'Super Heavy Lift',
    description: 'Fully reusable super heavy-lift launch system',
    domain: 'space',
    status: 'in_progress',
    importance: 10,
    tier: 1,
    trl: 6,
    dependencies: ['space-reusable-rockets'],
    keyPlayers: ['SpaceX'],
    progress: 70,
    milestones: [
      {
        date: '2024-10-13',
        title: 'Starship Booster Catch',
        description: 'First successful booster catch with chopsticks',
        significance: 'breakthrough',
        source: 'https://www.spacex.com/launches/mission/?missionId=starship-flight-5',
      },
    ],
  },

  // === INFRASTRUCTURE (Tier 2) ===
  {
    id: 'space-leo-stations',
    name: 'Commercial Space Stations',
    description: 'Private orbital facilities for research and tourism',
    domain: 'space',
    status: 'in_progress',
    importance: 7,
    tier: 2,
    trl: 5,
    dependencies: ['space-reusable-rockets'],
    keyPlayers: ['Axiom', 'Vast', 'Blue Origin'],
    progress: 40,
    yearEstimated: 2028,
    milestones: [
      {
        date: '2022-04-08',
        title: 'Axiom Mission 1',
        description: 'First fully private crew to ISS (Axiom Space)',
        significance: 'major',
        source: 'https://www.nasa.gov/exploration/commercial/crew/axiom-mission-1',
      },
    ],
  },
  {
    id: 'space-satellite-internet',
    name: 'Satellite Internet',
    description: 'Global broadband via LEO satellite constellations',
    domain: 'space',
    status: 'in_progress',
    importance: 7,
    tier: 2,
    trl: 7,
    dependencies: ['space-reusable-rockets'],
    keyPlayers: ['SpaceX Starlink', 'Amazon Kuiper', 'OneWeb'],
    progress: 75,
    progressMetric: 'Satellites Deployed',
    progressValue: '6,000+',
    milestones: [
      {
        date: '2020-10-24',
        title: 'Starlink Beta',
        description: 'SpaceX opens Starlink public beta in US and Canada',
        significance: 'major',
        source: 'https://www.spacex.com/updates/starlink-mission-10-24-2020',
      },
    ],
  },

  // === LUNAR (Tier 2-3) ===
  {
    id: 'space-lunar-return',
    name: 'Lunar Return',
    description: 'Humans back to the Moon after Apollo',
    domain: 'space',
    status: 'in_progress',
    importance: 8,
    tier: 2,
    trl: 5,
    dependencies: ['space-starship'],
    keyPlayers: ['NASA Artemis', 'SpaceX', 'Blue Origin'],
    progress: 45,
    yearEstimated: 2026,
    milestones: [
      {
        date: '2022-11-16',
        title: 'Artemis I',
        description: 'NASA uncrewed Orion flight around the Moon',
        significance: 'major',
        source: 'https://www.nasa.gov/mission_pages/artemis/artemis-i',
      },
    ],
  },
  {
    id: 'space-lunar-base',
    name: 'Permanent Lunar Base',
    description: 'Sustained human presence on the Moon',
    domain: 'space',
    status: 'theoretical',
    importance: 9,
    tier: 3,
    trl: 3,
    dependencies: ['space-lunar-return'],
    keyPlayers: ['NASA', 'ESA', 'CNSA'],
    yearEstimated: 2035,
    milestones: [
      {
        date: '2020-09-01',
        title: 'Artemis Accords',
        description: 'International framework for lunar exploration and utilization',
        significance: 'major',
        source: 'https://www.nasa.gov/specials/artemis-accords/',
      },
    ],
  },

  // === MARS (Tier 3-4) ===
  {
    id: 'space-mars-cargo',
    name: 'Mars Cargo Missions',
    description: 'Uncrewed supply missions to Mars',
    domain: 'space',
    status: 'theoretical',
    importance: 8,
    tier: 3,
    trl: 3,
    dependencies: ['space-starship'],
    keyPlayers: ['SpaceX'],
    yearEstimated: 2026,
    milestones: [
      {
        date: '2021-04-19',
        title: 'Ingenuity on Mars',
        description: 'First powered flight on another planet (Mars helicopter)',
        significance: 'major',
        source: 'https://www.nasa.gov/press-release/nasa-s-ingenuity-mars-helicopter-succeeds-in-historic-first-flight',
      },
    ],
  },
  {
    id: 'space-mars-human',
    name: 'Human Mars Landing',
    description: 'First humans on the surface of Mars',
    domain: 'space',
    status: 'theoretical',
    importance: 10,
    tier: 4,
    trl: 2,
    dependencies: ['space-mars-cargo'],
    keyPlayers: ['SpaceX', 'NASA'],
    yearEstimated: 2030,
    milestones: [
      {
        date: '2016-09-27',
        title: 'SpaceX Mars Architecture',
        description: 'Musk presents Interplanetary Transport System for Mars colonization',
        significance: 'major',
        source: 'https://www.spacex.com/news/2016/09/27/iac-mars-presentation',
      },
    ],
  },
  {
    id: 'space-mars-colony',
    name: 'Mars Colony',
    description: 'Self-sustaining human settlement on Mars',
    domain: 'space',
    status: 'speculative',
    importance: 10,
    tier: 5,
    trl: 1,
    dependencies: ['space-mars-human'],
    yearEstimated: 2050,
    milestones: [
      {
        date: '2020-01-01',
        title: 'Mars Society and Roadmaps',
        description: 'Advocacy and technical roadmaps for sustained Mars presence',
        significance: 'minor',
        source: 'https://www.marssociety.org/',
      },
    ],
  },

  // === ADVANCED (Tier 3+) ===
  {
    id: 'space-asteroid-mining',
    name: 'Asteroid Mining',
    description: 'Extracting resources from asteroids',
    domain: 'space',
    status: 'theoretical',
    importance: 8,
    tier: 3,
    trl: 2,
    dependencies: ['space-reusable-rockets'],
    keyPlayers: ['AstroForge', 'TransAstra'],
    yearEstimated: 2035,
    milestones: [
      {
        date: '2023-04-21',
        title: 'AstroForge Bro-1',
        description: 'First commercial mission to attempt asteroid prospecting',
        significance: 'major',
        source: 'https://astroforge.io/',
      },
    ],
  },
  {
    id: 'space-manufacturing',
    name: 'Space Manufacturing',
    description: 'Industrial production in microgravity',
    domain: 'space',
    status: 'in_progress',
    importance: 7,
    tier: 3,
    trl: 4,
    dependencies: ['space-leo-stations'],
    keyPlayers: ['Varda Space', 'Made In Space'],
    progress: 25,
    milestones: [
      {
        date: '2023-06-22',
        title: 'Varda W-1',
        description: 'Varda Space launches first in-space pharmaceutical manufacturing mission',
        significance: 'major',
        source: 'https://varda.com/',
      },
    ],
  },
  {
    id: 'space-interstellar',
    name: 'Interstellar Probes',
    description: 'Probes capable of reaching other star systems',
    domain: 'space',
    status: 'theoretical',
    importance: 8,
    tier: 5,
    trl: 1,
    dependencies: ['space-mars-colony'],
    keyPlayers: ['Breakthrough Starshot'],
    milestones: [
      {
        date: '2016-04-12',
        title: 'Breakthrough Starshot',
        description: 'Initiative announced for gram-scale probes to Alpha Centauri',
        significance: 'major',
        source: 'https://breakthroughinitiatives.org/initiative/3',
      },
    ],
  },
];

export default SPACE_NODES;
